import React from 'react';
import { ScheduledCommandPanelModelProps } from './modelTypes';
import {
  badgeClass,
  extractDecisionHistory,
  extractRejectHistory,
  fmtDateTime,
  isOfferAwaitingClient,
  latestRejectReason,
  opsStatusTone,
  pricingBadgeClass,
  requestDisplayStatus,
  requestSlaState,
  resolvePricingSource,
  routeAccent,
  statusTone,
} from './utils';

const identityChipClass = (tone: 'ok' | 'warn' | 'info') => {
  if (tone === 'ok') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (tone === 'info') return 'border-sky-200 bg-sky-50 text-sky-700';
  return 'border-rose-200 bg-rose-50 text-rose-700';
};

const ScheduledCommandPanelViews: React.FC<ScheduledCommandPanelModelProps> = ({ model }) => {
  const [expandedDecisionHistoryRequestId, setExpandedDecisionHistoryRequestId] = React.useState('');
  const {
    activeView,
    bookingPolicy,
    calendarDays,
    density,
    expandedPlanId,
    exportManifestCsv,
    filteredInventory,
    filteredRequests,
    filteredSeatPlan,
    focusInventoryInCalendar,
    focusRequestId,
    inventorySearch,
    laneByStatus,
    loading,
    monthSummary,
    monthTitle,
    myQueueOnly,
    offerFromRequest,
    openBookingDetail,
    openIssueTicket,
    openOfferReview,
    operationsDayPlans,
    requestSearch,
    requests,
    seatPlanDate,
    seatPlanSearch,
    selectCalendarDate,
    selectedDaySummary,
    setActiveView,
    setExpandedPlanId,
    setFlightOpsStatus,
    setInventorySearch,
    setMyQueueOnly,
    setOutcome,
    setRequestSearch,
    setSeatPlanDate,
    setSeatPlanSearch,
    setSelectedPlan,
    shiftCalendarMonth,
  } = model;

  return (
    <>
      {activeView === 'OPERATIONS' && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2">
              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-indigo-500">Month Flights</p>
              <p className="mt-1 text-xl font-black text-indigo-800">{monthSummary.flights}</p>
            </div>
            <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2">
              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-sky-500">Available Seats</p>
              <p className="mt-1 text-xl font-black text-sky-800">{monthSummary.availableSeats}</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-emerald-500">Sold Seats</p>
              <p className="mt-1 text-xl font-black text-emerald-800">{monthSummary.soldSeats}</p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-amber-500">Avg Load</p>
              <p className="mt-1 text-xl font-black text-amber-800">{monthSummary.avgLoadPct}%</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-600">Monthly Availability Calendar</p>
            <div className="flex items-center gap-2">
              <button onClick={() => shiftCalendarMonth(-1)} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-black text-slate-600">◀</button>
              <span className="text-xs font-black text-slate-700 min-w-[120px] text-center">{monthTitle}</span>
              <button onClick={() => shiftCalendarMonth(1)} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-black text-slate-600">▶</button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <div key={d} className="px-2 py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((d) => (
              <button
                key={d.iso}
                onClick={() => selectCalendarDate(d.iso)}
                className={`rounded-lg border px-2 py-2 text-left transition-all ${
                  d.inMonth ? 'border-slate-200' : 'bg-slate-100 border-slate-100 text-slate-400'
                } ${
                  d.slots > 0
                    ? 'bg-gradient-to-br from-sky-50 via-indigo-50 to-white'
                    : 'bg-white'
                } ${seatPlanDate === d.iso ? 'ring-2 ring-indigo-400 shadow-md' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-black">{d.day}</div>
                  {d.slots > 0 && (
                    <span className="rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[9px] font-black text-indigo-700">
                      {d.slots}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-[10px] font-semibold text-slate-600">{d.slots} flight(s)</div>
                <div className="text-[10px] font-semibold text-slate-500">Avail {d.availableSeats} · Sold {d.soldSeats}</div>
              </button>
            ))}
          </div>

          {seatPlanDate ? (
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="flex items-center justify-between bg-[linear-gradient(180deg,#f8fafc_0%,#eef2ff_100%)] px-4 py-3 border-b border-slate-200">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">Day Flight Options</p>
                  <p className="text-sm font-black text-slate-900">Flight Options · {seatPlanDate}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700">
                    {operationsDayPlans.length} flight(s)
                  </span>
                  <button
                    type="button"
                    onClick={() => setSeatPlanDate('')}
                    className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700"
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="sticky top-0 z-[2] border-b border-slate-200 bg-white/95 backdrop-blur px-3 py-2">
                <div className="grid grid-cols-2 md:grid-cols-7 gap-2 text-[11px]">
                  <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1"><span className="text-[10px] font-black uppercase tracking-[0.1em] text-indigo-600">Flights</span><p className="text-base font-black text-indigo-800">{selectedDaySummary.flights}</p></div>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1"><span className="text-[10px] font-black uppercase tracking-[0.1em] text-emerald-600">Sold</span><p className="text-base font-black text-emerald-800">{selectedDaySummary.soldSeats}</p></div>
                  <div className="rounded-lg border border-sky-200 bg-sky-50 px-2 py-1"><span className="text-[10px] font-black uppercase tracking-[0.1em] text-sky-600">Available</span><p className="text-base font-black text-sky-800">{selectedDaySummary.availableSeats}</p></div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1"><span className="text-[10px] font-black uppercase tracking-[0.1em] text-slate-500">Capacity</span><p className="text-base font-black text-slate-800">{selectedDaySummary.totalSeats}</p></div>
                  <div className="rounded-lg border border-violet-200 bg-violet-50 px-2 py-1"><span className="text-[10px] font-black uppercase tracking-[0.1em] text-violet-600">Load</span><p className="text-base font-black text-violet-800">{selectedDaySummary.loadPct}%</p></div>
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-1"><span className="text-[10px] font-black uppercase tracking-[0.1em] text-amber-600">Open To Book</span><p className="text-base font-black text-amber-800">{selectedDaySummary.openForBooking}</p></div>
                  <div className="rounded-lg border border-rose-200 bg-rose-50 px-2 py-1"><span className="text-[10px] font-black uppercase tracking-[0.1em] text-rose-600">Rejected</span><p className="text-base font-black text-rose-800">{selectedDaySummary.rejectedRequests}</p></div>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {operationsDayPlans.map((plan) => {
                  const soldPct = plan.totalSeats > 0 ? Math.round((plan.soldSeats / plan.totalSeats) * 100) : 0;
                  const dep = new Date(plan.departureAt);
                  const minHours = Math.max(0, Number(bookingPolicy.minHoursBeforeDeparture || 0));
                  const cutoff = new Date(dep.getTime() - minHours * 60 * 60 * 1000);
                  const closed = cutoff.getTime() < Date.now();
                  const accent = routeAccent(plan.fromCode, plan.toCode);
                  const opsStatus = String((plan as any).opsStatus || 'OPEN').toUpperCase() as 'OPEN' | 'CLOSED' | 'DEPARTED' | 'CANCELLED';
                  const canSell = !['CLOSED', 'DEPARTED', 'CANCELLED'].includes(opsStatus);
                  const isExpanded = expandedPlanId === plan.id;
                  const linkedRequestsForPlan = requests.filter((r) => r.matchedInventory?.id === plan.id);
                  return (
                    <div key={plan.id} className="p-3">
                      <div className={`w-full text-left rounded-2xl border ${accent.cardBorder} bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] px-4 py-4 shadow-[0_8px_25px_rgba(15,23,42,0.05)]`}>
                        <div className="flex flex-wrap items-start gap-3">
                          <div className="min-w-[220px]">
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${accent.routeTag}`}>Route</span>
                            <p className="mt-1 text-lg font-black tracking-tight text-slate-900">{plan.fromCode} ➔ {plan.toCode}</p>
                            <p className="text-xs font-semibold text-slate-500 mt-0.5">{fmtDateTime(plan.departureAt)}</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${badgeClass(statusTone(plan.status))}`}>{plan.status}</span>
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${badgeClass(opsStatusTone(opsStatus))}`}>OPS: {opsStatus}</span>
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${pricingBadgeClass(plan.pricingSource)}`}>{plan.pricingSource}</span>
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${closed ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                              {closed ? 'Booking Closed' : 'Open For Booking'}
                            </span>
                          </div>
                          <div className="ml-auto flex flex-wrap items-center gap-2">
                            <button
                              onClick={() => setExpandedPlanId((prev) => (prev === plan.id ? '' : plan.id))}
                              className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700 hover:bg-indigo-100"
                            >
                              {isExpanded ? 'Hide Detail' : 'Open Detail'}
                            </button>
                            <button
                              onClick={() => openIssueTicket(plan, 'CONFIRMED')}
                              disabled={!canSell}
                              className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-700 hover:bg-emerald-100"
                            >
                              Issue Ticket
                            </button>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px]">
                          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2"><span className="block text-[10px] font-black uppercase tracking-[0.1em] text-emerald-600">Sold</span><span className="text-base font-black text-emerald-800">{plan.soldSeats}</span></div>
                          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2"><span className="block text-[10px] font-black uppercase tracking-[0.1em] text-amber-600">Held</span><span className="text-base font-black text-amber-800">{plan.heldSeats}</span></div>
                          <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2"><span className="block text-[10px] font-black uppercase tracking-[0.1em] text-sky-600">Available</span><span className="text-base font-black text-sky-800">{plan.availableSeats}</span></div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"><span className="block text-[10px] font-black uppercase tracking-[0.1em] text-slate-500">Total</span><span className="text-base font-black text-slate-800">{plan.totalSeats}</span></div>
                          <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2"><span className="block text-[10px] font-black uppercase tracking-[0.1em] text-indigo-600">Load</span><span className="text-base font-black text-indigo-800">{soldPct}%</span></div>
                        </div>

                        {isExpanded && (
                          <>
                            <div className="mt-3 rounded-xl border border-slate-200 bg-white px-3 py-2">
                              <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                                <div
                                  className={`h-full rounded-full bg-gradient-to-r ${accent.progress} transition-all`}
                                  style={{ width: `${Math.max(0, Math.min(100, soldPct))}%` }}
                                />
                              </div>
                              <p className="mt-1 text-[11px] font-semibold text-slate-600">
                                Booking cutoff: <span className={`font-black ${closed ? 'text-rose-700' : 'text-emerald-700'}`}>{fmtDateTime(cutoff.toISOString())}</span>
                                {minHours > 0 ? ` (${minHours}h before departure)` : ' (until departure)'}
                              </p>
                            </div>

                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <button
                                onClick={() => setSelectedPlan(plan)}
                                className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700 hover:bg-indigo-100"
                              >
                                Flight Detail
                              </button>
                              <button
                                onClick={() => exportManifestCsv(plan)}
                                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
                              >
                                Export Manifest
                              </button>
                              <button
                                onClick={() => openIssueTicket(plan, 'HELD')}
                                disabled={!canSell}
                                className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-amber-700 hover:bg-amber-100"
                              >
                                Hold Seat
                              </button>
                              <button
                                onClick={() => setFlightOpsStatus(plan.id, 'OPEN')}
                                className="rounded-xl border border-emerald-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-700"
                              >
                                Open
                              </button>
                              <button
                                onClick={() => setFlightOpsStatus(plan.id, 'CLOSED')}
                                className="rounded-xl border border-amber-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-amber-700"
                              >
                                Close Sales
                              </button>
                            </div>

                            {linkedRequestsForPlan.length > 0 && (
                              <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50/40 overflow-hidden">
                                <div className="flex items-center justify-between bg-white/70 border-b border-indigo-100 px-3 py-2">
                                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700">
                                    Matched Requests / Offer Drafts ({linkedRequestsForPlan.length})
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => setActiveView('REQUESTS')}
                                    className="rounded-lg border border-indigo-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700"
                                  >
                                    Open Requests
                                  </button>
                                </div>
                                <div className="divide-y divide-indigo-100">
                                  {linkedRequestsForPlan.map((r) => {
                                    const status = requestDisplayStatus(r);
                                    const rejectHistory = extractRejectHistory(r as any);
                                    const rejectReason = latestRejectReason(r as any);
                                    const decisionHistory = extractDecisionHistory(r as any);
                                    const isHistoryOpen = expandedDecisionHistoryRequestId === r.id;
                                    const disableSend = isOfferAwaitingClient(status) || ['CLOSED_WON', 'CLOSED_LOST', 'DECLINED'].includes(status);
                                    return (
                                    <div key={r.id} className="px-3 py-2">
                                      <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] ${badgeClass(statusTone(status))}`}>{status}</span>
                                        <span className="text-[12px] font-black text-slate-800">{r.customerName || r.fromEmail || 'Unknown customer'}</span>
                                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] ${identityChipClass((r as any).customerId ? 'ok' : 'warn')}`}>
                                          {(r as any).customerId ? 'Linked' : 'Unlinked'}
                                        </span>
                                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] ${identityChipClass((r as any).customerId ? 'info' : 'warn')}`}>
                                          {(r as any).customerId ? 'Known Contact' : 'Needs Verification'}
                                        </span>
                                        <span className="text-[11px] font-semibold text-slate-500">PAX {r.pax || '—'}</span>
                                        {!!rejectReason && (
                                          <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] text-rose-700">
                                            Reject: {rejectReason}{rejectHistory.length > 1 ? ` (+${rejectHistory.length - 1})` : ''}
                                          </span>
                                        )}
                                        <span className="ml-auto flex items-center gap-2">
                                          <button
                                            type="button"
                                            onClick={() => {
                                              openOfferReview(r);
                                            }}
                                            className="rounded-lg border border-indigo-200 bg-white px-2 py-1 text-[9px] font-black uppercase tracking-[0.1em] text-indigo-700"
                                          >
                                            Review
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => void offerFromRequest(r)}
                                            disabled={disableSend}
                                            className="rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-[9px] font-black uppercase tracking-[0.1em] text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                                          >
                                            {disableSend ? 'Locked' : 'Send Offer'}
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => setExpandedDecisionHistoryRequestId((prev) => (prev === r.id ? '' : r.id))}
                                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[9px] font-black uppercase tracking-[0.1em] text-slate-700"
                                          >
                                            {isHistoryOpen ? 'Hide History' : `History (${decisionHistory.length})`}
                                          </button>
                                        </span>
                                      </div>
                                      {rejectHistory.length > 1 && !isHistoryOpen && (
                                        <div className="mt-1 text-[10px] font-semibold text-rose-700">
                                          Reject history: {rejectHistory.map((item) => item.reason).join(' | ')}
                                        </div>
                                      )}
                                      {isHistoryOpen && (
                                        <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-2 space-y-1">
                                          {decisionHistory.length ? decisionHistory.map((item, idx) => (
                                            <div key={`${r.id}-hist-${idx + 1}`} className="text-[10px] font-semibold text-slate-700">
                                              <span className="font-black text-slate-900">{item.event}</span>
                                              {item.at ? ` · ${fmtDateTime(item.at)}` : ''}
                                              {item.reason ? ` · ${item.reason}` : ''}
                                              {item.seats ? ` · Seats ${item.seats}` : ''}
                                              {typeof item.unitPriceUSD === 'number' ? ` · ${item.currencyCode || 'USD'} ${item.unitPriceUSD.toFixed(2)}` : ''}
                                              {item.actorName ? ` · by ${item.actorName}` : ''}
                                              {item.source ? ` · ${item.source}` : ''}
                                            </div>
                                          )) : (
                                            <div className="text-[10px] font-semibold text-slate-500">No decision timeline available.</div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )})}
                                </div>
                              </div>
                            )}

                            <div className="mt-3 rounded-xl border border-slate-200 bg-white overflow-hidden">
                              <div className="flex items-center justify-between bg-slate-50 border-b border-slate-200 px-3 py-2">
                                <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-600">
                                  Ticket Ledger ({plan.bookings.length})
                                </p>
                                <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">Click row to open</p>
                              </div>
                              {plan.bookings.length ? (
                                <div className="divide-y divide-slate-100">
                                  {plan.bookings.slice(0, 5).map((b) => (
                                    <button
                                      type="button"
                                      key={b.id}
                                      onClick={() => openBookingDetail(plan, b)}
                                      className="w-full text-left px-3 py-2 hover:bg-indigo-50"
                                    >
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className={`inline-flex w-fit rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] ${badgeClass(statusTone(b.status))}`}>{b.status}</span>
                                        <span className="truncate text-[12px] font-black text-slate-900">{b.customerName || b.customerEmail || 'Unknown customer'}</span>
                                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] ${identityChipClass((b as any).customerId ? 'ok' : 'warn')}`}>
                                          {(b as any).customerId ? 'Linked' : 'Unlinked'}
                                        </span>
                                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] ${identityChipClass((b as any).customerId ? 'info' : 'warn')}`}>
                                          {(b as any).customerId ? 'Known Contact' : 'Needs Verification'}
                                        </span>
                                        <span className="text-[11px] font-semibold text-slate-600">PAX {b.seats}</span>
                                        <span className="text-[11px] font-semibold text-slate-500">{fmtDateTime(b.createdAt)}</span>
                                        <span className="ml-auto text-[10px] font-black uppercase tracking-[0.12em] text-indigo-600">Open</span>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <p className="px-3 py-3 text-[11px] font-semibold text-slate-500">
                                  No ticket sold yet for this flight.
                                </p>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
                {!operationsDayPlans.length && <div className="p-4 text-sm text-slate-500">No flights for selected date.</div>}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">No day selected</p>
              <p className="mt-2 text-sm font-semibold text-slate-600">
                Click a date in the calendar to load flight options and ticket ledger.
              </p>
            </div>
          )}
        </div>
      )}

      {activeView === 'REQUESTS' && (
        <div className="rounded-2xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between bg-slate-100 px-4 py-3">
            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-600">Recent Requests</p>
            <span className="text-xs font-bold text-slate-500">{filteredRequests.length}</span>
          </div>
          <div className="px-3 py-2 border-b border-slate-200 bg-white">
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                placeholder="Search request by customer/email/route..."
                value={requestSearch}
                onChange={e => setRequestSearch(e.target.value)}
              />
              <button
                onClick={() => setMyQueueOnly((v) => !v)}
                className={`rounded-lg border px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] ${
                  myQueueOnly ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-600'
                }`}
              >
                My Queue {myQueueOnly ? '(ON)' : ''}
              </button>
            </div>
          </div>
          <div className={`${density === 'DENSE' ? 'max-h-[34rem]' : 'max-h-96'} overflow-auto divide-y divide-slate-100`}>
            {filteredRequests.map(r => (
              <div
                key={r.id}
                className={`${density === 'DENSE' ? 'p-2 space-y-1.5' : 'p-3 space-y-2'} ${
                  focusRequestId === r.id ? 'bg-indigo-50/60 ring-1 ring-indigo-200' : ''
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${badgeClass(statusTone(requestDisplayStatus(r)))}`}>{requestDisplayStatus(r)}</span>
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${badgeClass(statusTone(laneByStatus(requestDisplayStatus(r))))}`}>{laneByStatus(requestDisplayStatus(r))}</span>
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${badgeClass(requestSlaState(r).tone)}`}>{requestSlaState(r).label}</span>
                  <span className="text-xs font-bold text-slate-700">{r.customerName || r.fromEmail || 'Unknown'}</span>
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${identityChipClass((r as any).customerId ? 'ok' : 'warn')}`}>
                    {(r as any).customerId ? 'Linked' : 'Unlinked'}
                  </span>
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${identityChipClass((r as any).customerId ? 'info' : 'warn')}`}>
                    {(r as any).customerId ? 'Known Contact' : 'Needs Verification'}
                  </span>
                  <span className="text-xs text-slate-500">{r.fromCode || '—'} ➔ {r.toCode || '—'}</span>
                </div>
                <div className="text-[11px] text-slate-500">
                  PAX: {r.pax || '—'} | Travel: {fmtDateTime(r.travelDate)} | Updated: {fmtDateTime(r.updatedAt)} | Owner: {r.assignedToName || 'Unassigned'}
                </div>
                <div className="flex flex-wrap gap-2">
                  {r.matchedInventory?.id && (
                    <button onClick={() => openOfferReview(r)} className="rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700">Review</button>
                  )}
                  <button onClick={() => void offerFromRequest(r)} className="rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-700">Offer</button>
                  <button onClick={() => setOutcome(r.id, 'ALTERNATIVE_SENT')} className="rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-sky-700">Alternative</button>
                  <button onClick={() => setOutcome(r.id, 'PENDING_APPROVAL')} className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-amber-700">Escalate</button>
                  <button onClick={() => setOutcome(r.id, 'DECLINED')} className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-rose-700">Decline</button>
                  {r.matchedInventory?.departureAt && (
                    <button
                      onClick={() => focusInventoryInCalendar(r.matchedInventory || null)}
                      className="rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700"
                    >
                      Open Flight
                    </button>
                  )}
                </div>
              </div>
            ))}
            {!filteredRequests.length && !loading && <div className="p-4 text-sm text-slate-500">No scheduled requests found for current filters.</div>}
          </div>
        </div>
      )}

      {activeView === 'INVENTORY' && (
        <div className="rounded-2xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between bg-slate-100 px-4 py-3">
            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-600">Seat Inventory</p>
            <span className="text-xs font-bold text-slate-500">{filteredInventory.length}</span>
          </div>
          <div className="px-3 py-2 border-b border-slate-200 bg-white">
            <input
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
              placeholder="Search inventory by route/source/status..."
              value={inventorySearch}
              onChange={e => setInventorySearch(e.target.value)}
            />
          </div>
          <div className={`${density === 'DENSE' ? 'max-h-[34rem]' : 'max-h-96'} overflow-auto divide-y divide-slate-100`}>
            {filteredInventory.map(item => (
              <div key={item.id} className={`${density === 'DENSE' ? 'p-2 space-y-1.5' : 'p-3 space-y-2'}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${badgeClass(statusTone(item.status))}`}>{item.status}</span>
                  <span className="text-xs font-bold text-slate-700">{item.source}</span>
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${pricingBadgeClass(resolvePricingSource(item))}`}>
                    {resolvePricingSource(item)}
                  </span>
                  <span className="text-xs text-slate-500">{item.fromCode} ➔ {item.toCode}</span>
                </div>
                <div className="text-[11px] text-slate-500">
                  Dep: {fmtDateTime(item.departureAt)} | Seats: {item.availableSeats}/{item.totalSeats} | Price: {item.currencyCode} {item.seatPriceUSD}
                </div>
              </div>
            ))}
            {!filteredInventory.length && !loading && <div className="p-4 text-sm text-slate-500">No seat inventory found for current filters.</div>}
          </div>
        </div>
      )}

      {activeView === 'SEAT_PLAN' && (
        <div className="rounded-2xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between bg-slate-100 px-4 py-3">
            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-600">Seat Plan by Date / Time / Route</p>
            <span className="text-xs font-bold text-slate-500">{filteredSeatPlan.length}</span>
          </div>
          <div className="px-3 py-2 border-b border-slate-200 bg-white">
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                placeholder="Search seat plans by route, status, customer..."
                value={seatPlanSearch}
                onChange={e => setSeatPlanSearch(e.target.value)}
              />
              <input
                type="date"
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                value={seatPlanDate}
                onChange={e => setSeatPlanDate(e.target.value)}
              />
              {seatPlanDate && (
                <button
                  type="button"
                  onClick={() => setSeatPlanDate('')}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-slate-600"
                >
                  Clear Date
                </button>
              )}
            </div>
          </div>
          <div className={`${density === 'DENSE' ? 'max-h-[38rem]' : 'max-h-[28rem]'} overflow-auto divide-y divide-slate-100`}>
            {filteredSeatPlan.map((plan) => {
              const soldPct = plan.totalSeats > 0 ? Math.round((plan.soldSeats / plan.totalSeats) * 100) : 0;
              return (
                <div key={plan.id} className={density === 'DENSE' ? 'p-2' : 'p-3'}>
                  <div className="w-full text-left rounded-xl border border-slate-200 bg-white px-3 py-3 hover:bg-slate-50">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-black text-slate-900">{plan.fromCode} ➔ {plan.toCode}</span>
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${badgeClass(statusTone(plan.status))}`}>{plan.status}</span>
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${pricingBadgeClass(plan.pricingSource)}`}>{plan.pricingSource}</span>
                      <span className="text-[11px] font-semibold text-slate-500">{fmtDateTime(plan.departureAt)}</span>
                      <button
                        onClick={() => setSelectedPlan(plan)}
                        className="ml-auto rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700"
                      >
                        Details
                      </button>
                    </div>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px]">
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1"><span className="font-black text-emerald-700">SOLD:</span> {plan.soldSeats}</div>
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-1"><span className="font-black text-amber-700">HELD:</span> {plan.heldSeats}</div>
                      <div className="rounded-lg border border-sky-200 bg-sky-50 px-2 py-1"><span className="font-black text-sky-700">AVAILABLE:</span> {plan.availableSeats}</div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1"><span className="font-black text-slate-700">TOTAL:</span> {plan.totalSeats}</div>
                      <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1"><span className="font-black text-indigo-700">SOLD %:</span> {soldPct}%</div>
                    </div>
                  </div>
                </div>
              );
            })}
            {!filteredSeatPlan.length && !loading && (
              <div className="p-4 text-sm text-slate-500">No seat plan rows found for current filters.</div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ScheduledCommandPanelViews;
